import React, { useState, useEffect, useRef } from 'react';
import { useOutletContext } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  Space,
  Tag,
  Modal,
  Form,
  Input,
  Select,
  InputNumber,
  Switch,
  message,
  Popconfirm,
  Alert,
  Typography,
  Tooltip,
  Progress,
  Divider,
  Tabs,
  Spin,
  Grid,
  DatePicker
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  GlobalOutlined,
  CopyOutlined,
  LockOutlined,
  SafetyCertificateOutlined,
  LoadingOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SyncOutlined,
  UploadOutlined,
  MinusCircleOutlined
} from '@ant-design/icons';
import dayjs from 'dayjs';
import cdnService from '../../services/cdnService';

const { Option } = Select;
const { Text } = Typography;
const { TextArea } = Input;
const { useBreakpoint } = Grid;

export default function OriginSettings() {
  const { domainData, reloadDomain } = useOutletContext();
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const parentDomain = domainData?.domain || '';

  const stripDomain = (hostname) => {
    if (!hostname || !parentDomain) return hostname;
    if (hostname === parentDomain) return '@';
    const suffix = '.' + parentDomain;
    if (hostname.endsWith(suffix)) {
      return hostname.slice(0, -suffix.length);
    }
    return hostname;
  };

  const toFullHostname = (prefix) => {
    if (!prefix || !parentDomain) return prefix;
    const trimmed = prefix.trim();
    if (trimmed === '@') return parentDomain;
    if (trimmed === parentDomain) return parentDomain;
    const suffix = '.' + parentDomain;
    if (trimmed.endsWith(suffix)) {
      return trimmed;
    }
    return trimmed + suffix;
  };

  const [loading, setLoading] = useState(false);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingHostname, setEditingHostname] = useState(null);
  const [form] = Form.useForm();
  const [availableGroups, setAvailableGroups] = useState([]);
  const [groupsLoading, setGroupsLoading] = useState(false);

  // SSL Modal states
  const [sslModalVisible, setSslModalVisible] = useState(false);
  const [sslHostname, setSslHostname] = useState(null);
  const [sslLoading, setSslLoading] = useState(false);
  const [sslStatus, setSslStatus] = useState(null);
  const [sslForm] = Form.useForm();
  const sslPollingRef = useRef(null);

  useEffect(() => {
    loadGroups();
  }, []);

  const loadGroups = async () => {
    setGroupsLoading(true);
    try {
      const response = await cdnService.getCdnGroups(null, 'active');
      if (response.success) {
        setAvailableGroups(response.data || []);
      }
    } catch (error) {
      console.error('Failed to load CDN groups:', error);
    } finally {
      setGroupsLoading(false);
    }
  };

  const copyCname = (value) => {
    if (!value) return;
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(value)
        .then(() => message.success('已复制到剪贴板'))
        .catch(() => message.error('复制失败，请手动复制'));
    } else {
      const textArea = document.createElement('textarea');
      textArea.value = value;
      textArea.style.position = 'fixed';
      textArea.style.left = '-9999px';
      document.body.appendChild(textArea);
      textArea.select();
      try {
        document.execCommand('copy');
        message.success('已复制到剪贴板');
      } catch (err) {
        message.error('复制失败，请手动复制');
      }
      document.body.removeChild(textArea);
    }
  };

  const handleAdd = () => {
    form.resetFields();
    form.setFieldsValue({ origins: [{ address: '', port: undefined }] });
    setEditingHostname(null);
    setModalVisible(true);
  };

  // 将 origin 数据转换为 origins 列表格式（兼容新旧格式）
  const parseOriginToList = (origin, port) => {
    if (!origin) return [{ address: '', port: undefined, weight: undefined }];
    // 新格式: [{address, port, weight}, ...]
    if (Array.isArray(origin) && origin.length > 0 && typeof origin[0] === 'object' && origin[0].address) {
      return origin.map(o => ({ address: o.address, port: o.port || undefined, weight: o.weight || undefined }));
    }
    // 旧格式: 字符串数组或换行分隔字符串
    const list = Array.isArray(origin) ? origin : String(origin).split('\n').map(s => s.trim()).filter(Boolean);
    return list.length > 0
      ? list.map(addr => ({ address: addr, port: port || undefined, weight: undefined }))
      : [{ address: '', port: undefined, weight: undefined }];
  };

  const handleEdit = (hostname) => {
    setEditingHostname(hostname);
    form.setFieldsValue({
      ...hostname,
      hostname: stripDomain(hostname.hostname),
      origins: parseOriginToList(hostname.origin, hostname.port),
      migration_group_id: hostname.migration_group_id || undefined,
      migration_expires_at: hostname.migration_expires_at ? dayjs(hostname.migration_expires_at) : null,
      migration_notes: hostname.migration_notes
    });
    setModalVisible(true);
  };

  const handleDelete = async (hostnameId) => {
    setLoading(true);
    try {
      const response = await cdnService.deleteHostname(hostnameId);
      if (response.success) {
        message.success('删除成功');
        reloadDomain();
      } else {
        message.error(response.error || '删除失败');
      }
    } catch (error) {
      console.error('Failed to delete hostname:', error);
      message.error('删除失败');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      // 新格式: origins 列表 [{address, port}, ...]
      const originsList = (values.origins || []).filter(o => o && o.address);
      let originData;
      if (originsList.length === 0) {
        message.error('请添加至少一个回源地址');
        setLoading(false);
        return;
      }
      // 检查是否有自定义权重或不同端口
      const hasCustomWeight = originsList.some(o => o.weight && o.weight !== 100);
      const allSamePort = originsList.every(o => (o.port || null) === (originsList[0].port || null));
      if (!hasCustomWeight && allSamePort && originsList.length === 1) {
        originData = originsList[0].address;
      } else if (!hasCustomWeight && allSamePort) {
        originData = originsList.map(o => o.address);
      } else {
        // 使用新的 {address, port, weight} 格式
        originData = originsList.map(o => {
          const entry = { address: o.address, port: o.port || null };
          if (o.weight && o.weight !== 100) entry.weight = o.weight;
          return entry;
        });
      }

      const data = {
        ...values,
        hostname: toFullHostname(values.hostname),
        origin: originData,
        port: allSamePort ? (originsList[0].port || null) : null,
        domain_id: domainData.id,
        migration_group_id: values.migration_group_id || null,
        migration_expires_at: values.migration_expires_at
          ? values.migration_expires_at.format('YYYY-MM-DD HH:mm:ss')
          : null
      };
      // 移除 origins 字段（已转换为 origin）
      delete data.origins;

      if (!values.migration_notes) {
        delete data.migration_notes;
      }

      let response;
      if (editingHostname) {
        response = await cdnService.updateHostname({
          ...data,
          id: editingHostname.id
        });
      } else {
        response = await cdnService.createHostname(data);
      }

      if (response.success) {
        message.success(editingHostname ? '更新成功' : '添加成功');
        setModalVisible(false);
        reloadDomain();
      } else {
        message.error(response.error || '操作失败');
      }
    } catch (error) {
      console.error('Failed to save hostname:', error);
      message.error('操作失败');
    } finally {
      setLoading(false);
    }
  };

  // SSL Modal handlers
  const openSslModal = async (hostname) => {
    setSslHostname(hostname);
    setSslModalVisible(true);
    setSslLoading(true);
    try {
      const response = await cdnService.getHostnameAcmeStatus(hostname.id);
      if (response.success) {
        setSslStatus(response.data);
      }
    } catch (error) {
      console.error('Failed to get SSL status:', error);
    } finally {
      setSslLoading(false);
    }
  };

  const closeSslModal = () => {
    setSslModalVisible(false);
    setSslHostname(null);
    setSslStatus(null);
    if (sslPollingRef.current) {
      clearInterval(sslPollingRef.current);
      sslPollingRef.current = null;
    }
  };

  const handleEnableSsl = async (enabled, sslType = 'auto') => {
    if (!sslHostname) return;
    setSslLoading(true);
    try {
      const response = await cdnService.enableHostnameSsl(sslHostname.id, enabled, sslType);
      if (response.success) {
        message.success(enabled ? 'SSL 已启用' : 'SSL 已禁用');
        reloadDomain();
        const statusResponse = await cdnService.getHostnameAcmeStatus(sslHostname.id);
        if (statusResponse.success) {
          setSslStatus(statusResponse.data);
        }
      } else {
        message.error(response.error || '操作失败');
      }
    } catch (error) {
      console.error('Failed to toggle SSL:', error);
      message.error('操作失败');
    } finally {
      setSslLoading(false);
    }
  };

  const handleRequestAcme = async (validationType = 'http-01') => {
    if (!sslHostname) return;
    setSslLoading(true);
    try {
      const response = await cdnService.requestHostnameAcme(sslHostname.id, validationType);
      if (response.success) {
        message.success('ACME 证书申请已提交');
        reloadDomain();
        startSslStatusPolling();
      } else {
        message.error(response.error || '申请失败');
      }
    } catch (error) {
      console.error('Failed to request ACME:', error);
      message.error('申请失败');
    } finally {
      setSslLoading(false);
    }
  };

  const startSslStatusPolling = () => {
    if (sslPollingRef.current) {
      clearInterval(sslPollingRef.current);
    }
    sslPollingRef.current = setInterval(async () => {
      if (!sslHostname) {
        clearInterval(sslPollingRef.current);
        return;
      }
      try {
        const response = await cdnService.getHostnameAcmeStatus(sslHostname.id);
        if (response.success) {
          setSslStatus(response.data);
          if (response.data.acme_status === 'issued' || response.data.acme_status === 'failed') {
            clearInterval(sslPollingRef.current);
            sslPollingRef.current = null;
            if (response.data.acme_status === 'issued') {
              message.success('SSL 证书已签发');
              reloadDomain();
            }
          }
        }
      } catch (error) {
        console.error('Failed to poll SSL status:', error);
      }
    }, 5000);
  };

  const handleUploadCert = async (values) => {
    if (!sslHostname) return;
    setSslLoading(true);
    try {
      const response = await cdnService.uploadHostnameSsl(
        sslHostname.id,
        values.certificate,
        values.private_key
      );
      if (response.success) {
        message.success('证书上传成功');
        reloadDomain();
        const statusResponse = await cdnService.getHostnameAcmeStatus(sslHostname.id);
        if (statusResponse.success) {
          setSslStatus(statusResponse.data);
        }
      } else {
        message.error(response.error || '上传失败');
      }
    } catch (error) {
      console.error('Failed to upload cert:', error);
      message.error('上传失败');
    } finally {
      setSslLoading(false);
    }
  };

  const getSslStatusTag = (hostname) => {
    const status = hostname.ssl_status;
    if (!status || !status.enabled) {
      return <Tag>未启用</Tag>;
    }

    const acmeStatus = status.acme_status;
    if (status.type === 'custom') {
      return <Tag color="green" icon={<CheckCircleOutlined />}>自定义证书</Tag>;
    }

    switch (acmeStatus) {
      case 'pending':
        return <Tag color="processing" icon={<LoadingOutlined />}>等待中</Tag>;
      case 'verifying':
        return <Tag color="processing" icon={<SyncOutlined spin />}>验证中</Tag>;
      case 'issuing':
        return <Tag color="processing" icon={<SyncOutlined spin />}>签发中</Tag>;
      case 'issued':
        return <Tag color="success" icon={<CheckCircleOutlined />}>已签发</Tag>;
      case 'failed':
        return <Tag color="error" icon={<CloseCircleOutlined />}>失败</Tag>;
      default:
        return <Tag color="blue" icon={<LockOutlined />}>已启用</Tag>;
    }
  };

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      if (sslPollingRef.current) {
        clearInterval(sslPollingRef.current);
      }
    };
  }, []);

  // Desktop columns（精简，详细配置在编辑弹窗中查看）
  const columns = [
    {
      title: '主机名',
      dataIndex: 'hostname',
      key: 'hostname',
      width: 120,
      render: (text) => {
        const prefix = stripDomain(text);
        return (
          <Tooltip title={text}>
            <strong style={{ whiteSpace: 'nowrap' }}>{prefix}</strong>
          </Tooltip>
        );
      }
    },
    {
      title: '回源地址',
      dataIndex: 'origin',
      key: 'origin',
      width: 180,
      ellipsis: true,
      render: (origin, record) => {
        const origins = Array.isArray(origin) ? origin : [origin];
        const items = origins.filter(Boolean).map((o) => {
          if (typeof o === 'object' && o.address) {
            return { address: o.address, port: o.port, weight: o.weight };
          }
          return { address: String(o), port: record.port };
        });
        if (items.length === 0) return <Text type="secondary">未配置</Text>;
        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {items.map((item, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{
                  display: 'inline-block', width: 6, height: 6,
                  borderRadius: '50%', background: '#52c41a', flexShrink: 0
                }} />
                <Text style={{ fontSize: 13 }}>
                  {item.address}{item.port ? <Text type="secondary">:{item.port}</Text> : null}
                  {item.weight && item.weight !== 100 ? <Text type="secondary"> (w:{item.weight})</Text> : null}
                </Text>
              </div>
            ))}
          </div>
        );
      }
    },
    {
      title: 'CNAME',
      key: 'cname_target',
      width: 220,
      render: (_, record) => record.cname_target ? (
        <Space size={4} style={{ whiteSpace: 'nowrap' }}>
          <Text code style={{ fontSize: 11 }}>{record.cname_target}</Text>
          <Tooltip title="复制">
            <Button type="link" size="small" icon={<CopyOutlined />} onClick={() => copyCname(record.cname_target)} style={{ padding: 0 }} />
          </Tooltip>
        </Space>
      ) : (
        <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>保存后生成</Text>
      )
    },
    {
      title: '线路',
      key: 'migration',
      width: 130,
      render: (_, record) => record.effective_group ? (
        <span style={{ whiteSpace: 'nowrap' }}>
          <Tag color={record.effective_group.source === 'migration' ? 'orange' : 'blue'}>
            {record.effective_group.name || '默认'}
          </Tag>
          {record.effective_group.source === 'migration' && record.migration_active && (
            <Tag color="red">迁移</Tag>
          )}
        </span>
      ) : (
        <Text type="secondary" style={{ whiteSpace: 'nowrap' }}>默认</Text>
      )
    },
    {
      title: '状态',
      dataIndex: 'enabled',
      key: 'enabled',
      width: 70,
      render: (enabled) => (
        <Tag color={enabled ? 'success' : 'default'}>
          {enabled ? '启用' : '禁用'}
        </Tag>
      )
    },
    {
      title: 'SSL',
      key: 'ssl',
      width: 100,
      render: (_, record) => getSslStatusTag(record)
    },
    {
      title: '操作',
      key: 'action',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Space size={0}>
          <Button type="link" size="small" icon={<LockOutlined />} onClick={() => openSslModal(record)}>
            SSL
          </Button>
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => handleEdit(record)}>
            编辑
          </Button>
          <Popconfirm
            title="确定要删除这个源站吗?"
            onConfirm={() => handleDelete(record.id)}
            okText="确定"
            cancelText="取消"
          >
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              删除
            </Button>
          </Popconfirm>
        </Space>
      )
    }
  ];

  // Mobile card rendering
  const renderMobileCard = (item) => (
    <Card
      key={item.id}
      size="small"
      style={{ marginBottom: 12, overflow: 'hidden' }}
      title={
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          overflow: 'hidden',
          width: '100%'
        }}>
          <Tooltip title={item.hostname}>
            <span style={{
              fontSize: 14,
              flex: 1,
              minWidth: 0,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              {stripDomain(item.hostname)}
            </span>
          </Tooltip>
          <Tag color={item.enabled ? 'success' : 'default'} style={{ marginLeft: 8, flexShrink: 0 }}>
            {item.enabled ? '启用' : '禁用'}
          </Tag>
        </div>
      }
      styles={{
        header: { padding: '8px 12px' },
        body: { padding: '12px', overflow: 'hidden' }
      }}
    >
      <div style={{ fontSize: 12, marginBottom: 8 }}>
        <div style={{ color: '#666', marginBottom: 4 }}>回源地址:</div>
        <div style={{ background: '#f5f5f5', padding: '6px 10px', borderRadius: 6 }}>
          {(() => {
            const origins = Array.isArray(item.origin) ? item.origin : [item.origin];
            const items = origins.filter(Boolean).map(o => {
              if (typeof o === 'object' && o.address) return { address: o.address, port: o.port, weight: o.weight };
              return { address: String(o), port: item.port };
            });
            return items.map((o, i) => (
              <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '2px 0' }}>
                <span style={{ display: 'inline-block', width: 5, height: 5, borderRadius: '50%', background: '#52c41a', flexShrink: 0 }} />
                <span style={{ wordBreak: 'break-all' }}>
                  {o.address}{o.port ? <span style={{ color: '#999' }}>:{o.port}</span> : null}
                  {o.weight && o.weight !== 100 ? <span style={{ color: '#999' }}> (w:{o.weight})</span> : null}
                </span>
              </div>
            ));
          })()}
        </div>
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 8 }}>
        <Tag>{item.protocol}</Tag>
        <Tag>端口: {item.port || '默认'}</Tag>
        {getSslStatusTag(item)}
      </div>
      {item.effective_group && (
        <div style={{ marginBottom: 8 }}>
          <Tag color={item.effective_group.source === 'migration' ? 'orange' : 'blue'}>
            {item.effective_group.name || '默认线路'}
          </Tag>
          {item.effective_group.source === 'migration' && item.migration_active && (
            <Tag color="red">迁移中</Tag>
          )}
        </div>
      )}
      {item.cname_target && (
        <div style={{ fontSize: 12, marginBottom: 8 }}>
          <div style={{ color: '#666', marginBottom: 4 }}>CNAME:</div>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: 4,
            background: '#f5f5f5',
            padding: '4px 8px',
            borderRadius: 4,
            overflow: 'hidden'
          }}>
            <span style={{
              fontSize: 11,
              wordBreak: 'break-all',
              flex: 1,
              minWidth: 0,
              fontFamily: 'monospace'
            }}>
              {item.cname_target}
            </span>
            <Button
              type="link"
              size="small"
              icon={<CopyOutlined />}
              onClick={() => copyCname(item.cname_target)}
              style={{ padding: 0, flexShrink: 0 }}
            />
          </div>
        </div>
      )}
      <Divider style={{ margin: '8px 0' }} />
      <Space wrap size="small">
        <Button size="small" icon={<LockOutlined />} onClick={() => openSslModal(item)}>
          SSL
        </Button>
        <Button size="small" icon={<EditOutlined />} onClick={() => handleEdit(item)}>
          编辑
        </Button>
        <Popconfirm
          title="确定要删除这个源站吗?"
          onConfirm={() => handleDelete(item.id)}
          okText="确定"
          cancelText="取消"
        >
          <Button size="small" danger icon={<DeleteOutlined />}>
            删除
          </Button>
        </Popconfirm>
      </Space>
    </Card>
  );

  return (
    <div style={{ padding: isMobile ? '12px 0' : '24px 0' }}>
      <Card
        title={
          <Space>
            <GlobalOutlined />
            <span style={{ fontSize: isMobile ? 14 : 16 }}>源站设置</span>
          </Space>
        }
        size={isMobile ? 'small' : 'default'}
        extra={
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAdd}
            size={isMobile ? 'small' : 'middle'}
          >
            {isMobile ? '添加' : '添加源站'}
          </Button>
        }
      >
        <Alert
          message="配置说明"
          description={isMobile
            ? "CDN 从源站获取内容并缓存分发给用户。"
            : "源站是您实际的服务器地址。CDN 会从这些源站获取内容并缓存，然后分发给用户。您可以配置多个源站实现负载均衡和故障转移。"
          }
          type="info"
          showIcon
          closable
          style={{ marginBottom: 16 }}
        />

        {isMobile ? (
          <div>
            {(domainData.hostnames || []).map(item => renderMobileCard(item))}
            {(!domainData.hostnames || domainData.hostnames.length === 0) && (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#999' }}>
                暂无源站配置
              </div>
            )}
          </div>
        ) : (
          <Table
            columns={columns}
            dataSource={domainData.hostnames || []}
            rowKey="id"
            loading={loading}
            pagination={false}
            scroll={{ x: 1000 }}
            size="small"
          />
        )}
      </Card>

      {/* Add/Edit Modal */}
      <Modal
        title={editingHostname ? '编辑源站' : '添加源站'}
        open={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        confirmLoading={loading}
        width={isMobile ? '100%' : 700}
        style={isMobile ? { top: 20 } : undefined}
        styles={isMobile ? { body: { maxHeight: '70vh', overflow: 'auto' } } : undefined}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
          size={isMobile ? 'middle' : 'default'}
          initialValues={{
            protocol: 'auto',
            mode: 'roundrobin',
            http_version: '1.1',
            proxy_timeout: 60,
            cache_enable: 'auto',
            cache_expire: 3600,
            browser_cache: 'auto',
            browser_cache_expire: 7200,
            enabled: true,
            priority: 100
          }}
        >
          <Form.Item
            name="hostname"
            label="主机名"
            rules={[{ required: true, message: '请输入主机名' }]}
            extra="输入 @ 表示主域名本身，* 表示泛域名"
          >
            <Input
              placeholder="www"
              addonAfter={<span style={{ fontSize: 12 }}>.{parentDomain}</span>}
            />
          </Form.Item>

          <Form.Item label="回源地址" required>
            <Form.List
              name="origins"
              initialValue={[{ address: '', port: undefined }]}
              rules={[{
                validator: async (_, origins) => {
                  if (!origins || origins.length === 0 || !origins.some(o => o && o.address)) {
                    return Promise.reject(new Error('请添加至少一个回源地址'));
                  }
                }
              }]}
            >
              {(fields, { add, remove }, { errors }) => (
                <>
                  {fields.map(({ key, name, ...restField }) => (
                    <Space key={key} style={{ display: 'flex', marginBottom: 8, width: '100%' }} align="baseline">
                      <Form.Item
                        {...restField}
                        name={[name, 'address']}
                        rules={[{ required: true, message: '请输入地址' }]}
                        style={{ flex: 1, marginBottom: 0 }}
                      >
                        <Input placeholder="origin.example.com" style={{ width: isMobile ? 140 : 240 }} />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'port']}
                        style={{ marginBottom: 0 }}
                      >
                        <InputNumber min={1} max={65535} placeholder="端口" style={{ width: isMobile ? 70 : 90 }} />
                      </Form.Item>
                      <Form.Item
                        {...restField}
                        name={[name, 'weight']}
                        style={{ marginBottom: 0 }}
                      >
                        <InputNumber min={1} max={100} placeholder="权重" style={{ width: isMobile ? 70 : 80 }} />
                      </Form.Item>
                      {fields.length > 1 && (
                        <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f' }} />
                      )}
                    </Space>
                  ))}
                  <Form.Item style={{ marginBottom: 0 }}>
                    <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />} size={isMobile ? 'small' : 'middle'}>
                      添加源站地址
                    </Button>
                  </Form.Item>
                  <Form.ErrorList errors={errors} />
                </>
              )}
            </Form.List>
          </Form.Item>

          <Form.Item
            name="protocol"
            label="回源协议"
            rules={[{ required: true }]}
          >
            <Select>
              <Option value="auto">自动 (跟随请求)</Option>
              <Option value="http">HTTP</Option>
              <Option value="https">HTTPS</Option>
            </Select>
          </Form.Item>

          <Form.Item name="mode" label="负载均衡模式">
            <Select>
              <Option value="roundrobin">轮询</Option>
              <Option value="ip_hash">IP Hash</Option>
              <Option value="least_conn">最少连接</Option>
            </Select>
          </Form.Item>

          <Form.Item name="origin_host" label="回源 Host" tooltip="发给源站的 Host 头；留空 = 访问的主机名">
            <Input placeholder="留空则使用访问的主机名" allowClear />
          </Form.Item>
          <Form.Item name="origin_sni" label="回源 SNI" tooltip="HTTPS 回源时的 TLS SNI；留空 = 回源 Host，再空 = 访问的主机名">
            <Input placeholder="留空则跟随回源 Host" allowClear />
          </Form.Item>

          <Form.Item name="priority" label="优先级" extra="数值越小优先级越高">
            <InputNumber min={1} max={999} style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="enabled" label="启用" valuePropName="checked">
            <Switch />
          </Form.Item>

          <Divider orientation="left">线路迁移 / 防护</Divider>
          <Alert
            message="说明"
            description="当某个主机需要临时切换到特定线路（例如DDoS防护集群）时，可在此指定线路组及到期时间。"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />

          <Form.Item name="migration_group_id" label="迁移到线路组">
            <Select
              allowClear
              placeholder="默认使用套餐线路"
              loading={groupsLoading}
            >
              {availableGroups.map(group => (
                <Option key={group.id} value={group.id}>
                  {group.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Form.Item name="migration_expires_at" label="迁移截止时间" extra="留空则需手动恢复">
            <DatePicker showTime style={{ width: '100%' }} />
          </Form.Item>

          <Form.Item name="migration_notes" label="迁移备注">
            <Input.TextArea rows={2} placeholder="记录迁移原因或操作人" />
          </Form.Item>
        </Form>
      </Modal>

      {/* SSL Modal */}
      <Modal
        title={
          <Space>
            <SafetyCertificateOutlined />
            <span style={{ fontSize: isMobile ? 14 : 16 }}>
              SSL 证书配置
            </span>
          </Space>
        }
        open={sslModalVisible}
        onCancel={closeSslModal}
        footer={null}
        width={isMobile ? '100%' : 700}
        style={isMobile ? { top: 20 } : undefined}
        styles={isMobile ? { body: { maxHeight: '70vh', overflow: 'auto' } } : undefined}
      >
        {isMobile && sslHostname && (
          <div style={{ marginBottom: 12, wordBreak: 'break-all', color: '#666' }}>
            {sslHostname.hostname}
          </div>
        )}
        {sslLoading && !sslStatus ? (
          <div style={{ textAlign: 'center', padding: '40px 0' }}>
            <Spin size="large" />
            <div style={{ marginTop: 16 }}>加载中...</div>
          </div>
        ) : (
          <div>
            {/* Current Status */}
            <Card size="small" style={{ marginBottom: 16 }}>
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  <Text strong>当前状态: </Text>
                  {sslHostname && getSslStatusTag(sslHostname)}
                </div>
                {sslStatus?.certificate && (
                  <div>
                    <Text strong>证书到期: </Text>
                    <Text>{sslStatus.certificate.expires_at}</Text>
                  </div>
                )}
                {sslStatus?.acme_error && (
                  <Alert
                    type="error"
                    message="证书申请失败"
                    description={sslStatus.acme_error}
                    showIcon
                    style={{ marginTop: 8 }}
                  />
                )}
              </Space>
            </Card>

            {/* ACME Progress */}
            {sslStatus?.acme_status && ['pending', 'verifying', 'issuing'].includes(sslStatus.acme_status) && (
              <Card size="small" title="ACME 证书申请进度" style={{ marginBottom: 16 }}>
                <Space direction="vertical" style={{ width: '100%' }}>
                  <Progress
                    percent={
                      sslStatus.acme_status === 'pending' ? 20 :
                      sslStatus.acme_status === 'verifying' ? 50 :
                      sslStatus.acme_status === 'issuing' ? 80 : 0
                    }
                    status="active"
                    size={isMobile ? 'small' : 'default'}
                  />
                  <div>
                    <Tag color="processing" icon={<SyncOutlined spin />}>
                      {sslStatus.acme_status === 'pending' ? '等待处理' :
                       sslStatus.acme_status === 'verifying' ? '域名验证中' :
                       sslStatus.acme_status === 'issuing' ? '证书签发中' : '处理中'}
                    </Tag>
                  </div>
                </Space>
              </Card>
            )}

            <Divider />

            <Tabs
              size={isMobile ? 'small' : 'default'}
              items={[
                {
                  key: 'auto',
                  label: isMobile ? '自动证书' : "自动证书 (Let's Encrypt)",
                  children: (
                    <div>
                      <Alert
                        message="免费自动证书"
                        description={isMobile
                          ? "使用 Let's Encrypt 自动申请证书，完全免费。"
                          : "使用 Let's Encrypt 自动申请和续期 SSL 证书，完全免费。证书有效期90天，系统会自动续期。"
                        }
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                      />
                      <Space direction="vertical" style={{ width: '100%' }}>
                        <div>
                          <Text strong>验证方式:</Text>
                          <div style={{ marginTop: 8 }}>
                            <Button
                              type="primary"
                              icon={<SafetyCertificateOutlined />}
                              loading={sslLoading}
                              onClick={() => handleRequestAcme('http-01')}
                              disabled={sslStatus?.acme_status && ['pending', 'verifying', 'issuing'].includes(sslStatus.acme_status)}
                              size={isMobile ? 'small' : 'middle'}
                            >
                              {sslStatus?.certificate ? '续费证书' : 'HTTP-01 验证'}
                            </Button>
                            <div style={{ marginTop: 4 }}>
                              <Text type="secondary" style={{ fontSize: 12 }}>
                                需要域名已解析到 CDN
                              </Text>
                            </div>
                          </div>
                        </div>
                        {sslStatus?.ssl_enabled && (
                          <div style={{ marginTop: 16 }}>
                            <Button
                              danger
                              onClick={() => handleEnableSsl(false)}
                              loading={sslLoading}
                              size={isMobile ? 'small' : 'middle'}
                            >
                              禁用 SSL
                            </Button>
                          </div>
                        )}
                      </Space>
                    </div>
                  )
                },
                {
                  key: 'custom',
                  label: '自定义证书',
                  children: (
                    <div>
                      <Alert
                        message="上传自定义证书"
                        description="上传您自己购买的 SSL 证书。"
                        type="info"
                        showIcon
                        style={{ marginBottom: 16 }}
                      />
                      <Form
                        form={sslForm}
                        layout="vertical"
                        onFinish={handleUploadCert}
                        size={isMobile ? 'middle' : 'default'}
                      >
                        <Form.Item
                          name="certificate"
                          label="证书 (PEM格式)"
                          rules={[{ required: true, message: '请输入证书内容' }]}
                        >
                          <TextArea
                            rows={isMobile ? 4 : 6}
                            placeholder="-----BEGIN CERTIFICATE-----"
                          />
                        </Form.Item>
                        <Form.Item
                          name="private_key"
                          label="私钥 (PEM格式)"
                          rules={[{ required: true, message: '请输入私钥内容' }]}
                        >
                          <TextArea
                            rows={isMobile ? 4 : 6}
                            placeholder="-----BEGIN PRIVATE KEY-----"
                          />
                        </Form.Item>
                        <Form.Item>
                          <Button
                            type="primary"
                            htmlType="submit"
                            icon={<UploadOutlined />}
                            loading={sslLoading}
                            size={isMobile ? 'small' : 'middle'}
                          >
                            上传证书
                          </Button>
                        </Form.Item>
                      </Form>
                    </div>
                  )
                }
              ]}
            />
          </div>
        )}
      </Modal>
    </div>
  );
}
